import React from "react";
import {
  Box,
  Card,
  Divider,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { CKEditor } from "ckeditor4-react";
import AccessSystemDocumentImageField from "./AccessSystemDocumentImageField";

const PDF_INFO_BLOCK_CKEDITOR_CONFIG = {
  toolbar: [
    ["Bold", "Italic"],
    ["NumberedList", "BulletedList"],
    ["Link", "Unlink"],
    ["Undo", "Redo"],
  ],
  height: 220,
  contentsCss: [
    `${process.env.PUBLIC_URL}/fonts/noir-pro/styles.css`,
    `${process.env.PUBLIC_URL}/vendors/ck-editor/style.css`,
  ],
  versionCheck: false,
};

/**
 * PDF ticket layout settings (info text, logo, bottom image) for processed-order printouts.
 */
const AccessSystemPdfTicketSettingsTab = ({
  values,
  setFieldValue,
  setFieldTouched,
  handleBlur,
  infoBlockEditorKey,
  thankYouDescriptionEditorKey,
  t,
}) => {
  const pdf = values.pdfTicketSettings || {};

  const infoBlockLabel = t(
    "pages.accessSystemSettings.pdfTicketSettings.fields.infoBlock",
    {
      defaultValue: "Info Blok",
    },
  );
  const infoBlockHelp = t(
    "pages.accessSystemSettings.pdfTicketSettings.fields.infoBlockHelp",
    {
      defaultValue: "Tento text sa zobrazi nad QR kodom",
    },
  );

  return (
    <Card sx={{ p: 2 }}>
      <Stack spacing={3}>
        <Typography variant="h6" fontWeight={700}>
          {t("pages.accessSystemSettings.pdfTicketSettings.pageTitle", {
            defaultValue: "Nastavenie PDF lístka",
          })}
        </Typography>
        <Divider />

        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("pages.accessSystemSettings.pdfTicketSettings.sections.thankYou", {
            defaultValue: "Poďakovanie (len obrazovka)",
          })}
        </Typography>
        <TextField
          fullWidth
          name="pdfTicketSettings.thankYouTitle"
          value={pdf.thankYouTitle ?? ""}
          onChange={(e) =>
            setFieldValue("pdfTicketSettings.thankYouTitle", e.target.value)
          }
          onBlur={handleBlur}
          label={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.thankYouTitle",
            {
              defaultValue: "Thank you title",
            },
          )}
          helperText={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.thankYouTitleHelp",
            {
              defaultValue:
                "Nadpis len na obrazovke spracovanej objednávky (netlačí sa na lístok).",
            },
          )}
        />
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {t(
              "pages.accessSystemSettings.pdfTicketSettings.fields.thankYouDescription",
              {
                defaultValue: "Thank you description",
              },
            )}
          </Typography>
          <Box sx={{ width: "100%", "& .cke": { maxWidth: "100%" } }}>
            <CKEditor
              key={`pdf-thank-you-desc-${thankYouDescriptionEditorKey}`}
              name="pdfTicketSettings.thankYouDescription"
              initData={pdf.thankYouDescription ?? ""}
              config={PDF_INFO_BLOCK_CKEDITOR_CONFIG}
              onChange={(event) => {
                const data = event.editor.getData();
                setFieldValue("pdfTicketSettings.thankYouDescription", data);
              }}
              onBlur={() =>
                setFieldTouched("pdfTicketSettings.thankYouDescription", true)
              }
            />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {t(
              "pages.accessSystemSettings.pdfTicketSettings.fields.thankYouDescriptionHelp",
              {
                defaultValue:
                  "Text s formátovaním len na uvítacej obrazovke (netlačí sa na lístok, HTML).",
              },
            )}
          </Typography>
        </Stack>

        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("pages.accessSystemSettings.pdfTicketSettings.sections.infoContent", {
            defaultValue: "Obsah informácií",
          })}
        </Typography>
        <Stack spacing={1}>
          <Typography variant="subtitle2" color="text.secondary">
            {infoBlockLabel}
          </Typography>
          <Box sx={{ width: "100%", "& .cke": { maxWidth: "100%" } }}>
            <CKEditor
              key={`pdf-info-block-${infoBlockEditorKey}`}
              name="pdfTicketSettings.infoBlock"
              initData={pdf.infoBlock ?? ""}
              config={PDF_INFO_BLOCK_CKEDITOR_CONFIG}
              onChange={(event) => {
                const data = event.editor.getData();
                setFieldValue("pdfTicketSettings.infoBlock", data);
              }}
              onBlur={() =>
                setFieldTouched("pdfTicketSettings.infoBlock", true)
              }
            />
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">
            {infoBlockHelp}
          </Typography>
        </Stack>

        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("pages.accessSystemSettings.pdfTicketSettings.sections.branding", {
            defaultValue: "Branding",
          })}
        </Typography>
        <AccessSystemDocumentImageField
          fieldPath="pdfTicketSettings.logoUrl"
          value={pdf.logoUrl ?? ""}
          setFieldValue={setFieldValue}
          label={t("pages.accessSystemSettings.pdfTicketSettings.fields.logo", {
            defaultValue: "Logo",
          })}
          helperText={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.logoHelp",
            {
              defaultValue:
                "Vyberte logo z dokumentov zákazníka (rovnaký spôsob ako v nastaveniach aplikácie).",
            },
          )}
        />

        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("pages.accessSystemSettings.pdfTicketSettings.sections.bottomImage", {
            defaultValue: "Obrázok pod QR kódom",
          })}
        </Typography>
        <AccessSystemDocumentImageField
          fieldPath="pdfTicketSettings.bottomImageUrl"
          value={pdf.bottomImageUrl ?? ""}
          setFieldValue={setFieldValue}
          label={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.bottomImage",
            {
              defaultValue: "Obrázok pod QR kódom",
            },
          )}
          helperText={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.bottomImageHelp",
            {
              defaultValue:
                "Môže slúžiť napríklad ako mapa areálu alebo orientačný plán. V PDF bude na celú šírku strany s maximálnou výškou 10 cm.",
            },
          )}
        />

        <Typography variant="subtitle1" fontWeight={600} color="text.primary">
          {t("pages.accessSystemSettings.pdfTicketSettings.sections.footer", {
            defaultValue: "Pätička dokumentu",
          })}
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          name="pdfTicketSettings.footerText"
          value={pdf.footerText ?? ""}
          onChange={(e) =>
            setFieldValue("pdfTicketSettings.footerText", e.target.value)
          }
          onBlur={handleBlur}
          label={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.footerText",
            {
              defaultValue: "Footer text",
            },
          )}
          helperText={t(
            "pages.accessSystemSettings.pdfTicketSettings.fields.footerTextHelp",
            {
              defaultValue:
                "Tento text sa zobrazí na konci PDF dokumentu a zároveň sa použije ako footerText pri tlači lístku.",
            },
          )}
        />
      </Stack>
    </Card>
  );
};

export default AccessSystemPdfTicketSettingsTab;
